import { useEffect, useMemo, useState } from 'react';
import { get, ref } from 'firebase/database';
import { db } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useStudentWorkspace } from '../../contexts/LmsDataContext';
import { Spinner } from '../ui/Spinner';
import Card from '../ui/Card';
import { WorkspaceShell } from '../ui/Workspace';
import {
  StudentBadge,
  StudentCard,
  StudentEmpty,
  StudentPage,
  StudentSection,
} from '../student/StudentUI';
import { formatStudentDate } from '../../utils/studentExperience';
import { buildScheduledLessons, buildStudentCalendarEvents } from '../../utils/studentSchedule';

const toArray = (value) => Object.values(value || {});

const toneByType = {
  class: 'accent',
  upcoming_class: 'soft',
  attendance_present: 'success',
  attendance_absent: 'warning',
  assignment_due: 'warning',
  assignment_done: 'success',
};

const labelByType = {
  class: 'Class',
  upcoming_class: 'Class',
  attendance_present: 'Present',
  attendance_absent: 'Absent',
  assignment_due: 'Due',
  assignment_done: 'Submitted',
};

const shortLabelByType = {
  class: 'Class',
  upcoming_class: 'Next',
  attendance_present: 'In',
  attendance_absent: 'Out',
  assignment_due: 'Due',
  assignment_done: 'Done',
};

const weekdayLabels = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const SchedulePage = () => {
  const { currentUser } = useAuth();
  const isStudent = currentUser?.role === 'student';
  const { data, loading } = useStudentWorkspace();
  const [attendance, setAttendance] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [metaLoading, setMetaLoading] = useState(true);
  const [visibleMonth, setVisibleMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [selectedDateKey, setSelectedDateKey] = useState('');

  useEffect(() => {
    const loadMeta = async () => {
      if (!isStudent || !currentUser?.uid) {
        setMetaLoading(false);
        return;
      }

      try {
        const [attendanceSnapshot, assignmentsSnapshot] = await Promise.all([
          get(ref(db, `attendance/${currentUser.uid}`)),
          get(ref(db, `assignments/${currentUser.uid}`)),
        ]);

        setAttendance(toArray(attendanceSnapshot.val()));
        setAssignments(toArray(assignmentsSnapshot.val()));
      } catch (error) {
        console.error('Error loading schedule meta:', error);
      } finally {
        setMetaLoading(false);
      }
    };

    loadMeta();
  }, [currentUser?.uid, isStudent]);

  const activeCourses = useMemo(() => data?.activeCourses || [], [data]);
  const calendarEvents = useMemo(
    () =>
      buildStudentCalendarEvents({
        activeCourses,
        attendance,
        assignments,
      }),
    [activeCourses, attendance, assignments],
  );

  const eventsByDate = useMemo(
    () =>
      calendarEvents.reduce((acc, event) => {
        acc[event.dateKey] = acc[event.dateKey] || [];
        acc[event.dateKey].push(event);
        return acc;
      }, {}),
    [calendarEvents],
  );

  const upcomingClasses = useMemo(() => {
    const lessons = [];

    activeCourses.forEach((course) => {
      buildScheduledLessons({ course, enrollment: course.enrollment })
        .filter((lesson) => lesson.isUpcoming)
        .forEach((lesson) => {
          lessons.push({
            id: `${course.id}-${lesson.lessonIndex}`,
            courseId: course.id,
            courseName: course.name,
            title: lesson.title || `Lesson ${lesson.lessonIndex + 1}`,
            date: lesson.scheduledDateIso,
            time: lesson.scheduledTime,
          });
        });
    });

    return lessons.sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [activeCourses]);

  useEffect(() => {
    if (selectedDateKey || !calendarEvents.length) return;
    setSelectedDateKey(calendarEvents[0].dateKey);
  }, [calendarEvents, selectedDateKey]);

  const calendarDays = useMemo(() => buildMonthGrid(visibleMonth), [visibleMonth]);
  const selectedDayEvents = selectedDateKey ? eventsByDate[selectedDateKey] || [] : [];
  const monthSummary = useMemo(() => {
    const visibleMonthKey = `${visibleMonth.getFullYear()}-${String(visibleMonth.getMonth() + 1).padStart(
      2,
      '0',
    )}`;
    const monthEvents = calendarEvents.filter((event) => event.dateKey.startsWith(visibleMonthKey));

    return {
      classDays: monthEvents.filter(
        (event) => event.type === 'class' || event.type === 'upcoming_class',
      ).length,
      attendanceMarked: monthEvents.filter(
        (event) => event.type === 'attendance_present' || event.type === 'attendance_absent',
      ).length,
      deadlines: monthEvents.filter(
        (event) => event.type === 'assignment_due' || event.type === 'assignment_done',
      ).length,
    };
  }, [calendarEvents, visibleMonth]);

  if (!isStudent) {
    return (
      <WorkspaceShell
        title="Schedule"
        description="Your teaching calendar and sessions will appear here."
      >
        <Card className="shadow-sm">
          <p className="text-sm text-gray-500">Schedule page coming soon...</p>
        </Card>
      </WorkspaceShell>
    );
  }

  if (loading || metaLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <StudentPage
      title="Class schedule"
      subtitle="Your timetable, attendance history, and assignment deadlines are kept together in one place."
    >
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.24fr)_minmax(18rem,22rem)]">
        <StudentSection
          title="Calendar"
          description="Tap a marked date to see the class, attendance, or deadline details."
          action={
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setVisibleMonth(getCurrentMonth())}
                className="hidden min-h-[38px] rounded-2xl border border-stone-300 bg-white px-3 text-sm font-semibold text-stone-700 transition hover:bg-stone-50 sm:inline-flex sm:items-center"
              >
                Today
              </button>
              <button
                type="button"
                onClick={() => setVisibleMonth(previousMonth(visibleMonth))}
                className="inline-flex min-h-[38px] min-w-[38px] items-center justify-center rounded-2xl border border-stone-300 bg-white text-stone-700 transition hover:bg-stone-50"
              >
                ‹
              </button>
              <span className="text-sm font-semibold text-stone-900">
                {visibleMonth.toLocaleDateString('en-US', {
                  month: 'long',
                  year: 'numeric',
                })}
              </span>
              <button
                type="button"
                onClick={() => setVisibleMonth(nextMonth(visibleMonth))}
                className="inline-flex min-h-[38px] min-w-[38px] items-center justify-center rounded-2xl border border-stone-300 bg-white text-stone-700 transition hover:bg-stone-50"
              >
                ›
              </button>
            </div>
          }
        >
          {!calendarEvents.length ? (
            <StudentEmpty
              title="No schedule yet"
              message="Your timetable will appear here once batches, lessons, or assignments are ready."
            />
          ) : (
            <div className="space-y-4">
              <div className="grid gap-2 sm:grid-cols-3">
                <SummaryChip label="Class items" value={monthSummary.classDays} />
                <SummaryChip label="Attendance marks" value={monthSummary.attendanceMarked} />
                <SummaryChip label="Deadlines" value={monthSummary.deadlines} />
              </div>

              <div className="flex flex-wrap gap-2">
                <LegendPill label="Class" type="class" />
                <LegendPill label="Upcoming" type="upcoming_class" />
                <LegendPill label="Present" type="attendance_present" />
                <LegendPill label="Absent" type="attendance_absent" />
                <LegendPill label="Due" type="assignment_due" />
                <LegendPill label="Done" type="assignment_done" />
              </div>

              <div className="grid grid-cols-7 gap-1.5 text-center text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500 md:gap-2">
                {weekdayLabels.map((day) => (
                  <div key={day} className="py-1.5">
                    {day}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1.5 md:gap-2">
                {calendarDays.map((day, index) => {
                  const dateKey = day ? toDateKey(day) : '';
                  const dayEvents = dateKey ? eventsByDate[dateKey] || [] : [];
                  const isSelected = selectedDateKey === dateKey;
                  const isCurrentMonth = day ? day.getMonth() === visibleMonth.getMonth() : false;
                  const eventTypes = [...new Set(dayEvents.map((event) => event.type))].slice(0, 2);

                  return (
                    <button
                      key={dateKey || `empty-${index}`}
                      type="button"
                      onClick={() => day && setSelectedDateKey(dateKey)}
                      disabled={!day}
                      className={`min-h-[4.8rem] rounded-[18px] border px-2 py-2 text-left transition md:min-h-[5.5rem] md:px-2.5 ${
                        !day
                          ? 'border-transparent bg-transparent'
                          : isSelected
                          ? 'border-[#f4b6d2] bg-[#fdf2f8]'
                          : 'border-stone-200 bg-white hover:border-stone-300 hover:bg-stone-50'
                      }`}
                    >
                      {day ? (
                        <div className="flex h-full flex-col">
                          <span
                            className={`text-sm font-semibold ${
                              isCurrentMonth ? 'text-stone-900' : 'text-stone-400'
                            }`}
                          >
                            {day.getDate()}
                          </span>
                          <div className="mt-2 flex flex-wrap gap-1">
                            {eventTypes.map((type) => (
                              <span
                                key={type}
                                className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold leading-none ${badgeClass(type)}`}
                              >
                                {shortLabelByType[type]}
                              </span>
                            ))}
                            {dayEvents.length > 2 ? (
                              <span className="inline-flex items-center rounded-full bg-stone-100 px-1.5 py-0.5 text-[9px] font-semibold leading-none text-stone-600">
                                +{dayEvents.length - 2}
                              </span>
                            ) : null}
                          </div>
                          <div className="mt-auto pt-2">
                            {dayEvents.length ? (
                              <p className="text-[11px] font-medium leading-tight text-stone-500">
                                {dayEvents.length} item{dayEvents.length > 1 ? 's' : ''}
                              </p>
                            ) : null}
                          </div>
                        </div>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </StudentSection>

        <div className="space-y-4">
          <StudentSection title="Selected day">
            {!selectedDayEvents.length ? (
              <StudentEmpty
                title="No item on this date"
                message="Choose a marked date to see the related classes, attendance, or deadlines."
              />
            ) : (
              <div className="space-y-2.5">
                {selectedDayEvents.map((event) => (
                  <StudentCard key={event.id} className="bg-white px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-stone-900">{event.title}</p>
                        <p className="mt-1 text-sm text-stone-500">
                          {event.subtitle}
                          {event.time ? ` • ${event.time}` : ''}
                        </p>
                      </div>
                      <StudentBadge tone={toneByType[event.type]}>
                        {labelByType[event.type]}
                      </StudentBadge>
                    </div>
                  </StudentCard>
                ))}
              </div>
            )}
          </StudentSection>

          <StudentSection title="Next classes">
            {!upcomingClasses.length ? (
              <StudentEmpty
                title="No upcoming class"
                message="Your next class will show here once the timetable is ready."
              />
            ) : (
              <div className="space-y-2.5">
                {upcomingClasses.slice(0, 4).map((lesson) => (
                  <StudentCard key={lesson.id} className="bg-white px-4 py-3">
                    <div className="space-y-1">
                      <p className="text-sm font-semibold text-stone-900">{lesson.title}</p>
                      <p className="text-sm text-stone-500">{lesson.courseName}</p>
                      <p className="text-sm text-stone-600">
                        {formatStudentDate(lesson.date)}
                        {lesson.time ? ` • ${lesson.time}` : ''}
                      </p>
                    </div>
                  </StudentCard>
                ))}
              </div>
            )}
          </StudentSection>
        </div>
      </div>
    </StudentPage>
  );
};

const SummaryChip = ({ label, value }) => (
  <div className="rounded-[18px] border border-stone-200 bg-stone-50 px-4 py-3">
    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-stone-500">{label}</p>
    <p className="mt-1 text-2xl font-semibold tracking-[-0.03em] text-stone-950">{value}</p>
  </div>
);

const LegendPill = ({ label, type }) => (
  <span
    className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-[11px] font-semibold ${badgeClass(type)}`}
  >
    <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold leading-none ${badgeClass(type)}`}>
      {shortLabelByType[type]}
    </span>
    {label}
  </span>
);

const badgeClass = (type) => {
  switch (type) {
    case 'class':
      return 'bg-[#fce7f3] text-[#b91c66]';
    case 'upcoming_class':
      return 'bg-stone-100 text-stone-700';
    case 'attendance_present':
      return 'bg-emerald-100 text-emerald-700';
    case 'attendance_absent':
      return 'bg-amber-100 text-amber-700';
    case 'assignment_due':
      return 'bg-amber-100 text-amber-700';
    case 'assignment_done':
      return 'bg-emerald-100 text-emerald-700';
    default:
      return 'bg-stone-100 text-stone-700';
  }
};

const toDateKey = (date) => date.toISOString().split('T')[0];

const buildMonthGrid = (monthDate) => {
  const firstDay = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const lastDay = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
  const days = [];

  for (let i = 0; i < firstDay.getDay(); i += 1) {
    days.push(null);
  }

  for (let day = 1; day <= lastDay.getDate(); day += 1) {
    days.push(new Date(monthDate.getFullYear(), monthDate.getMonth(), day));
  }

  while (days.length % 7 !== 0) {
    days.push(null);
  }

  return days;
};

const getCurrentMonth = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
};

const previousMonth = (date) => new Date(date.getFullYear(), date.getMonth() - 1, 1);
const nextMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 1);

export default SchedulePage;
